/**
 * API-key sign-in link — `/?apikey=vox_…` (alias `?api_key=`).
 *
 * A customer API key in the URL becomes the browser's session: it is moved
 * from the address bar into localStorage on the first render (App.tsx calls
 * `consumeApiKeyQueryParam()` at module load, before React mounts) and the
 * AuthnMe provider then treats the browser as signed in to that key's
 * customer, sending `X-API-Key` instead of `Authorization: Bearer`. The key
 * has no expiry of its own; the session lasts until the key is revoked on the
 * Customer page or the browser signs out (which only forgets the key).
 *
 * Intended for hands-off access by people who cannot complete an AuthnMe
 * login (an App Review team, an auditor): create a throwaway customer, mint a
 * key there, hand out the link, revoke the key when done. Anyone holding the
 * URL is that customer, so keep such workspaces small.
 */

export const API_KEY_STORAGE_KEY = 'voxcore_api_key';

/** Query parameter names accepted for the key. */
const API_KEY_QUERY_PARAMS = ['apikey', 'api_key'];

/** Customer API keys are `vox_` + 64 hex chars (bot_config_api.go:
 *  generateCustomerAPIKey); accept anything vox_-prefixed and hex-ish so a
 *  future key length change doesn't strand links. */
export function isVoxApiKey(value: string | null | undefined): value is string {
  return !!value && /^vox_[0-9a-fA-F]{16,}$/.test(value.trim());
}

/**
 * Read an API key from the current URL, persist it, and strip it from the
 * address bar (so it never lands in bookmarks, screenshots or referrers).
 * Returns the key when one was consumed, else null. A malformed value is
 * dropped from the URL but not stored.
 */
export function consumeApiKeyQueryParam(): string | null {
  if (typeof window === 'undefined') return null;
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return null;
  }
  let found: string | null = null;
  let touched = false;
  for (const name of API_KEY_QUERY_PARAMS) {
    const raw = url.searchParams.get(name);
    if (raw === null) continue;
    touched = true;
    if (!found && isVoxApiKey(raw)) found = raw.trim();
    url.searchParams.delete(name);
  }
  if (found) storeApiKey(found);
  if (touched) {
    try {
      window.history.replaceState({}, '', url.toString());
    } catch {
      /* history may be unavailable (sandboxed iframe) — the key is stored regardless */
    }
  }
  return found;
}

export function getStoredApiKey(): string | null {
  try {
    const value = localStorage.getItem(API_KEY_STORAGE_KEY);
    return isVoxApiKey(value) ? value : null;
  } catch {
    return null;
  }
}

export function storeApiKey(key: string): void {
  try {
    localStorage.setItem(API_KEY_STORAGE_KEY, key.trim());
  } catch {
    /* storage blocked — the session will simply not persist */
  }
}

export function clearStoredApiKey(): void {
  try {
    localStorage.removeItem(API_KEY_STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** `vox_1234…abcdef` — for user-facing labels; never show the whole key. */
export function maskApiKey(key: string): string {
  const k = key.trim();
  if (k.length <= 14) return k;
  return `${k.slice(0, 8)}…${k.slice(-6)}`;
}
